import "./FinanceFilterDropdown.scss";

import type { OptionType } from "components/inputs";
import {
  DATE_TO_STRING,
  DatePicker,
  Select,
  STRING_TO_DATE,
} from "components/inputs";
import MultiSelect from "components/inputs/Select/MultiSelect";
import { INVOICE_STATUS_OPTIONS } from "constants/InvoiceConstants";
import debounce from "lodash/debounce";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useGetRresQuery } from "services/rre-api";
import type { QuickDateRange } from "utils/date-utils";
import { getQuickDateRange, QUICK_DATE_RANGE_OPTIONS } from "utils/date-utils";
import { useConstants } from "utils/hooks";

import { FILTER_CATEGORIES, shouldShowFilter } from "../../constants";
import type { FilterCategory, FinanceFilters } from "../../types";

// filter button that toggles the dropdown
type FinanceFilterButtonProps = {
  onClick: () => void;
  isOpen: boolean;
  hasActiveFilters?: boolean;
};

export const FinanceFilterButton: React.FC<FinanceFilterButtonProps> = ({
  onClick,
  isOpen,
  hasActiveFilters = false,
}) => {
  return (
    <button
      className={`finance-filter-button ${isOpen || hasActiveFilters ? "open" : ""}`}
      onClick={onClick}
      type="button"
    >
      <span className="material-icons">filter_list</span>
      <span className="button-text">Filter</span>
    </button>
  );
};

type FinanceFilterDropdownProps = {
  isOpen: boolean;
  onClose: () => void;
  filters: FinanceFilters;
  onUpdateFilter: (key: keyof FinanceFilters, value: unknown) => void;
  onUpdateFilters: (updates: Partial<FinanceFilters>) => void;
  clientOptions: OptionType[];
  fileOptions: OptionType[];
  invoiceOptions: OptionType[];
  isClientsFetching: boolean;
  isFilesFetching: boolean;
  isInvoicesFetching: boolean;
  onClientSearch: (value: string) => void;
  onFileSearch: (value: string) => void;
  onInvoiceSearch: (value: string) => void;
};

// dropdown that appears when the Filter button is clicked
const FinanceFilterDropdown: React.FC<FinanceFilterDropdownProps> = ({
  isOpen,
  onClose,
  filters,
  onUpdateFilter,
  onUpdateFilters,
  clientOptions,
  fileOptions,
  invoiceOptions,
  isClientsFetching,
  isFilesFetching,
  isInvoicesFetching,
  onClientSearch,
  onFileSearch,
  onInvoiceSearch,
}) => {
  const { constants } = useConstants();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedCategory, setExpandedCategory] =
    useState<FilterCategory | null>(null);

  // report type options
  const reportTypeOptions: OptionType[] = [
    { label: "Invoice List", value: "INVOICE_LIST" },
    { label: "Client List", value: "CLIENT_LIST_REPORT" },
    { label: "File List", value: "FILE_LIST" },
    { label: "Invoice Line Item Report", value: "INVOICE_LINE_ITEM_REPORT" },
    { label: "Work in Progress", value: "WORK_IN_PROGRESS" },
    { label: "Invoice Payment Report", value: "INVOICE_PAYMENT_REPORT" },
    { label: "By Service Report", value: "BY_SERVICE_REPORT" },
    { label: "Bulk Billing Report", value: "BULK_BILLING_REPORT" },
    { label: "Revenue by Client Report", value: "REVENUE_BY_CLIENT_REPORT" },
    { label: "No Services Report", value: "NO_SERVICES_REPORT" },
    { label: "Empty Files Report", value: "EMPTY_FILES_REPORT" },
    { label: "AI Job Billing Report", value: "AI_JOB_BILLING_REPORT" },
  ];

  const serviceTypeOptions = constants?.SERVICE_TYPE || [];

  const paymentStatusOptions: OptionType[] = [
    { label: "Paid", value: "Paid" },
    { label: "Outstanding", value: "Outstanding" },
  ];

  // Section 111 options
  const [rreSearchTerm, setRreSearchTerm] = useState<string>("");
  const debouncedSetRreSearchTerm = useMemo(
    () => debounce((value: string) => setRreSearchTerm(value), 300),
    [],
  );
  const handleRreInputChange = useCallback(
    (value: string) => {
      debouncedSetRreSearchTerm(value);
    },
    [debouncedSetRreSearchTerm],
  );
  const isSection111 =
    filters.report_type?.value?.toString() === "SECTION_111_INVOICING";
  const { data: rresData, isFetching: isRresFetching } = useGetRresQuery(
    { searchTerm: rreSearchTerm, filters: {} },
    { skip: !isSection111 || !rreSearchTerm.trim() },
  );
  const rreOptions = useMemo(() => {
    if (!rresData?.results) return [];
    return rresData.results.map((rre) => ({
      label: `${rre.name} (${rre.rre_id})`,
      value: rre.id,
    }));
  }, [rresData]);

  const fileTypeOptions: OptionType[] = [
    { label: "Claim", value: "C" },
    { label: "Query", value: "Q" },
  ];

  const actionFilterOptions: OptionType[] = [
    { label: "Add", value: "0" },
    { label: "Delete", value: "1" },
    { label: "Update", value: "2" },
  ];

  // handle quick date range selection
  const handleQuickDateRange = (
    range: QuickDateRange,
    startKey: keyof FinanceFilters,
    endKey: keyof FinanceFilters,
  ) => {
    const { start, end } = getQuickDateRange(range);
    onUpdateFilters({
      [startKey]: DATE_TO_STRING(start),
      [endKey]: DATE_TO_STRING(end),
    });
  };

  // render quick date shortcuts
  const renderQuickDateShortcuts = (
    startKey: keyof FinanceFilters,
    endKey: keyof FinanceFilters,
  ) => (
    <div className="quick-date-shortcuts">
      {QUICK_DATE_RANGE_OPTIONS.map((option) => (
        <button
          key={option.value}
          type="button"
          className="quick-date-button"
          onClick={() => handleQuickDateRange(option.value, startKey, endKey)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );

  // handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // check if click is inside the dropdown
      if (dropdownRef.current && dropdownRef.current.contains(target)) {
        return;
      }

      // check if click is on a date picker calendar (which is portaled)
      const clickedElement = event.target as HTMLElement;
      if (
        clickedElement.closest(".react-datepicker") ||
        clickedElement.closest(".react-datepicker-popper") ||
        clickedElement.closest(".react-datepicker__portal")
      ) {
        return;
      }

      // check if click is on a select menu (which is portaled)
      if (
        clickedElement.closest('[class*="kaboodle__menu"]') ||
        clickedElement.closest('[class*="select__menu"]')
      ) {
        return;
      }

      // if none of the above, close the dropdown
      onClose();
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // filter categories based on search and report type
  const visibleCategories = Object.keys(FILTER_CATEGORIES).filter((key) => {
    const category = key as FilterCategory;
    const config = FILTER_CATEGORIES[category];

    // exclude report_type from dropdown since it's now a separate selector
    if (category === "report_type") {
      return false;
    }

    // check if filter is available for current report type
    if (
      !shouldShowFilter(
        category,
        filters.report_type?.value?.toString() || null,
      )
    ) {
      return false;
    }

    // filter by search term
    if (searchTerm) {
      return config.label.toLowerCase().includes(searchTerm.toLowerCase());
    }
    return true;
  }) as FilterCategory[];

  const handleCategoryClick = (category: FilterCategory) => {
    if (expandedCategory === category) {
      setExpandedCategory(null);
    } else {
      setExpandedCategory(category);
    }
  };

  const renderCategoryContent = (category: FilterCategory) => {
    switch (category) {
      case "report_type":
        return (
          <div className="filter-dropdown-content">
            <Select
              name="report_type"
              label=""
              options={reportTypeOptions}
              value={filters.report_type}
              onChange={(value) => onUpdateFilter("report_type", value)}
              placeholder="Select report type..."
            />
          </div>
        );

      case "posted_dates":
        return (
          <div className="filter-dropdown-content">
            {renderQuickDateShortcuts("posted_date_start", "posted_date_end")}
            <DatePicker
              name="posted_date_start"
              label="From"
              initialDate={STRING_TO_DATE(filters.posted_date_start)}
              onDateChange={(date) => {
                onUpdateFilter("posted_date_start", DATE_TO_STRING(date));
              }}
              allowFutureDates={true}
            />
            <DatePicker
              name="posted_date_end"
              label="To"
              initialDate={STRING_TO_DATE(filters.posted_date_end)}
              onDateChange={(date) => {
                onUpdateFilter("posted_date_end", DATE_TO_STRING(date));
              }}
              allowFutureDates={true}
            />
          </div>
        );

      case "service_dates":
        return (
          <div className="filter-dropdown-content">
            {renderQuickDateShortcuts("service_date_from", "service_date_to")}
            <DatePicker
              name="service_date_from"
              label="From"
              initialDate={STRING_TO_DATE(filters.service_date_from)}
              onDateChange={(date) => {
                onUpdateFilter("service_date_from", DATE_TO_STRING(date));
              }}
              allowFutureDates={true}
            />
            <DatePicker
              name="service_date_to"
              label="To"
              initialDate={STRING_TO_DATE(filters.service_date_to)}
              onDateChange={(date) => {
                onUpdateFilter("service_date_to", DATE_TO_STRING(date));
              }}
              allowFutureDates={true}
            />
          </div>
        );

      case "clients":
        return (
          <div className="filter-dropdown-content">
            <MultiSelect
              name="clients"
              label=""
              options={clientOptions}
              value={filters.clients}
              placeholder="Search clients by name or ID..."
              noOptionsMessage={({ inputValue }) =>
                !inputValue
                  ? "Begin typing to search for clients..."
                  : isClientsFetching
                    ? "Searching..."
                    : "No clients found."
              }
              onInputChange={onClientSearch}
              onChange={(value) => onUpdateFilter("clients", value)}
              widthVariant="full"
              disableRemoveCondition={false}
            />
          </div>
        );

      case "service_types":
        return (
          <div className="filter-dropdown-content">
            <MultiSelect
              name="service_types"
              label=""
              options={serviceTypeOptions}
              value={filters.service_types}
              onChange={(value) => onUpdateFilter("service_types", value)}
              placeholder="Select service types..."
              widthVariant="full"
              disableRemoveCondition={false}
            />
          </div>
        );

      case "file_ids":
        return (
          <div className="filter-dropdown-content">
            <MultiSelect
              name="file_ids"
              label=""
              options={fileOptions}
              value={filters.file_ids}
              placeholder="Search for files..."
              noOptionsMessage={({ inputValue }) =>
                !inputValue
                  ? "Begin typing to search for files..."
                  : isFilesFetching
                    ? "Searching..."
                    : "No files found."
              }
              onInputChange={onFileSearch}
              onChange={(value) => onUpdateFilter("file_ids", value)}
              widthVariant="full"
              disableRemoveCondition={false}
            />
          </div>
        );

      case "invoice_statuses":
        return (
          <div className="filter-dropdown-content">
            <MultiSelect
              name="invoice_statuses"
              label=""
              options={INVOICE_STATUS_OPTIONS}
              value={filters.invoice_statuses}
              onChange={(value) => onUpdateFilter("invoice_statuses", value)}
              placeholder="Select invoice statuses..."
              widthVariant="full"
              disableRemoveCondition={false}
            />
          </div>
        );

      case "payment_status":
        return (
          <div className="filter-dropdown-content">
            <Select
              name="payment_status"
              label=""
              options={paymentStatusOptions}
              value={filters.payment_status}
              onChange={(value) => onUpdateFilter("payment_status", value)}
              placeholder="Select payment status..."
            />
          </div>
        );

      case "invoice_numbers":
        return (
          <div className="filter-dropdown-content">
            <MultiSelect
              name="invoice_numbers"
              label=""
              options={invoiceOptions}
              value={filters.invoice_numbers}
              placeholder="Search for invoices..."
              noOptionsMessage={({ inputValue }) =>
                !inputValue
                  ? "Begin typing to search for invoices..."
                  : isInvoicesFetching
                    ? "Searching..."
                    : "No invoices found."
              }
              onInputChange={onInvoiceSearch}
              onChange={(value) => onUpdateFilter("invoice_numbers", value)}
              widthVariant="full"
              disableRemoveCondition={false}
            />
          </div>
        );

      case "payment_dates":
        return (
          <div className="filter-dropdown-content">
            {renderQuickDateShortcuts("payment_date_from", "payment_date_to")}
            <DatePicker
              name="payment_date_from"
              label="From"
              initialDate={STRING_TO_DATE(filters.payment_date_from)}
              onDateChange={(date) => {
                onUpdateFilter("payment_date_from", DATE_TO_STRING(date));
              }}
              allowFutureDates={true}
            />
            <DatePicker
              name="payment_date_to"
              label="To"
              initialDate={STRING_TO_DATE(filters.payment_date_to)}
              onDateChange={(date) => {
                onUpdateFilter("payment_date_to", DATE_TO_STRING(date));
              }}
              allowFutureDates={true}
            />
          </div>
        );

      case "service_created_dates":
        return (
          <div className="filter-dropdown-content">
            {renderQuickDateShortcuts(
              "service_created_from",
              "service_created_to",
            )}
            <DatePicker
              name="service_created_from"
              label="From"
              initialDate={STRING_TO_DATE(filters.service_created_from)}
              onDateChange={(date) => {
                onUpdateFilter("service_created_from", DATE_TO_STRING(date));
              }}
              allowFutureDates={true}
            />
            <DatePicker
              name="service_created_to"
              label="To"
              initialDate={STRING_TO_DATE(filters.service_created_to)}
              onDateChange={(date) => {
                onUpdateFilter("service_created_to", DATE_TO_STRING(date));
              }}
              allowFutureDates={true}
            />
          </div>
        );

      case "file_opened_dates":
        return (
          <div className="filter-dropdown-content">
            {renderQuickDateShortcuts(
              "file_opened_on_from",
              "file_opened_on_to",
            )}
            <DatePicker
              name="file_opened_on_from"
              label="From"
              initialDate={STRING_TO_DATE(filters.file_opened_on_from)}
              onDateChange={(date) => {
                onUpdateFilter("file_opened_on_from", DATE_TO_STRING(date));
              }}
              allowFutureDates={true}
            />
            <DatePicker
              name="file_opened_on_to"
              label="To"
              initialDate={STRING_TO_DATE(filters.file_opened_on_to)}
              onDateChange={(date) => {
                onUpdateFilter("file_opened_on_to", DATE_TO_STRING(date));
              }}
              allowFutureDates={true}
            />
          </div>
        );

      case "icn":
        return (
          <div className="filter-dropdown-content">
            <input
              type="text"
              value={filters.icn || ""}
              onChange={(e) => {
                const val = e.target.value.trim();
                onUpdateFilter("icn", val || null);
              }}
              placeholder="Enter ICN..."
              className="filter-search-input"
              style={{ marginTop: 0 }}
            />
          </div>
        );

      case "claim_id":
        return (
          <div className="filter-dropdown-content">
            <input
              type="text"
              value={filters.claim_id || ""}
              onChange={(e) => {
                const val = e.target.value.trim();
                onUpdateFilter("claim_id", val || null);
              }}
              placeholder="Enter Claim ID..."
              className="filter-search-input"
              style={{ marginTop: 0 }}
            />
          </div>
        );

      case "claim_number":
        return (
          <div className="filter-dropdown-content">
            <input
              type="text"
              value={filters.claim_number || ""}
              onChange={(e) => {
                const val = e.target.value.trim();
                onUpdateFilter("claim_number", val || null);
              }}
              placeholder="Enter Claim Number..."
              className="filter-search-input"
              style={{ marginTop: 0 }}
            />
          </div>
        );

      case "rre_name":
        return (
          <div className="filter-dropdown-content">
            <input
              type="text"
              value={filters.rre_name || ""}
              onChange={(e) => {
                const val = e.target.value.trim();
                onUpdateFilter("rre_name", val || null);
              }}
              placeholder="Enter RRE Name..."
              className="filter-search-input"
              style={{ marginTop: 0 }}
            />
          </div>
        );

      case "rre_id":
        return (
          <div className="filter-dropdown-content">
            <input
              type="text"
              value={filters.rre_id || ""}
              onChange={(e) => {
                const val = e.target.value.trim();
                onUpdateFilter("rre_id", val || null);
              }}
              placeholder="Enter RRE ID..."
              className="filter-search-input"
              style={{ marginTop: 0 }}
            />
          </div>
        );

      case "s111_claimant_name":
        return (
          <div className="filter-dropdown-content">
            <input
              type="text"
              value={filters.s111_claimant_name || ""}
              onChange={(e) => {
                const val = e.target.value.trim();
                onUpdateFilter("s111_claimant_name", val || null);
              }}
              placeholder="Enter S111 Claimant Name..."
              className="filter-search-input"
              style={{ marginTop: 0 }}
            />
          </div>
        );

      case "s111_rre":
        return (
          <div className="filter-dropdown-content">
            <Select
              name="rre"
              label=""
              options={rreOptions}
              value={
                filters.rre
                  ? rreOptions.find((o) => o.value === filters.rre?.value) ||
                    filters.rre
                  : null
              }
              onChange={(value) => {
                onUpdateFilter("rre", value as OptionType | null);
              }}
              onInputChange={handleRreInputChange}
              noOptionsMessage={({ inputValue }) =>
                !inputValue
                  ? "Begin typing to search for RREs..."
                  : isRresFetching
                    ? "Searching..."
                    : "No RREs found."
              }
              isClearable
              placeholder="Search RREs..."
            />
          </div>
        );

      case "s111_file_type":
        return (
          <div className="filter-dropdown-content">
            <Select
              name="file_type"
              label=""
              options={fileTypeOptions}
              value={filters.file_type}
              onChange={(value) => {
                const option = value as OptionType | null;
                if (option?.value !== "C") {
                  onUpdateFilters({
                    file_type: option,
                    nsa_filter: null,
                    action_filter: null,
                  });
                } else {
                  onUpdateFilter("file_type", option);
                }
              }}
              isClearable
              placeholder="Select file type..."
            />
          </div>
        );

      case "s111_send_dates": {
        // key on the wrapper forces full remount of both DatePickers when
        // values change externally (quick shortcuts, pill removal)
        const sendDatesKey = `${filters.send_date_from || ""}_${filters.send_date_to || ""}`;
        return (
          <div className="filter-dropdown-content" key={sendDatesKey}>
            {renderQuickDateShortcuts("send_date_from", "send_date_to")}
            <DatePicker
              name="send_date_from"
              label="From"
              initialDate={STRING_TO_DATE(filters.send_date_from)}
              onDateChange={(date) => {
                onUpdateFilter("send_date_from", DATE_TO_STRING(date));
              }}
              allowFutureDates={true}
            />
            <DatePicker
              name="send_date_to"
              label="To"
              initialDate={STRING_TO_DATE(filters.send_date_to)}
              onDateChange={(date) => {
                onUpdateFilter("send_date_to", DATE_TO_STRING(date));
              }}
              allowFutureDates={true}
            />
          </div>
        );
      }

      case "s111_doi_dates": {
        const doiKey = `${filters.doi_from || ""}_${filters.doi_to || ""}`;
        return (
          <div className="filter-dropdown-content" key={doiKey}>
            {renderQuickDateShortcuts("doi_from", "doi_to")}
            <DatePicker
              name="doi_from"
              label="From"
              initialDate={STRING_TO_DATE(filters.doi_from)}
              onDateChange={(date) => {
                onUpdateFilter("doi_from", DATE_TO_STRING(date));
              }}
              allowFutureDates={true}
            />
            <DatePicker
              name="doi_to"
              label="To"
              initialDate={STRING_TO_DATE(filters.doi_to)}
              onDateChange={(date) => {
                onUpdateFilter("doi_to", DATE_TO_STRING(date));
              }}
              allowFutureDates={true}
            />
          </div>
        );
      }

      case "s111_tpoc_dates": {
        const tpocKey = `${filters.tpoc_from || ""}_${filters.tpoc_to || ""}`;
        return (
          <div className="filter-dropdown-content" key={tpocKey}>
            {renderQuickDateShortcuts("tpoc_from", "tpoc_to")}
            <DatePicker
              name="tpoc_from"
              label="From"
              initialDate={STRING_TO_DATE(filters.tpoc_from)}
              onDateChange={(date) => {
                onUpdateFilter("tpoc_from", DATE_TO_STRING(date));
              }}
              allowFutureDates={true}
            />
            <DatePicker
              name="tpoc_to"
              label="To"
              initialDate={STRING_TO_DATE(filters.tpoc_to)}
              onDateChange={(date) => {
                onUpdateFilter("tpoc_to", DATE_TO_STRING(date));
              }}
              allowFutureDates={true}
            />
          </div>
        );
      }

      case "s111_medicare_dates": {
        const medicareKey = `${filters.medicare_from || ""}_${filters.medicare_to || ""}`;
        return (
          <div className="filter-dropdown-content" key={medicareKey}>
            {renderQuickDateShortcuts("medicare_from", "medicare_to")}
            <DatePicker
              name="medicare_from"
              label="From"
              initialDate={STRING_TO_DATE(filters.medicare_from)}
              onDateChange={(date) => {
                onUpdateFilter("medicare_from", DATE_TO_STRING(date));
              }}
              allowFutureDates={true}
            />
            <DatePicker
              name="medicare_to"
              label="To"
              initialDate={STRING_TO_DATE(filters.medicare_to)}
              onDateChange={(date) => {
                onUpdateFilter("medicare_to", DATE_TO_STRING(date));
              }}
              allowFutureDates={true}
            />
          </div>
        );
      }

      case "s111_hide_invoiced":
        return (
          <div className="filter-dropdown-content">
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "4px 0",
                cursor: "pointer",
                fontSize: "14px",
              }}
            >
              <input
                type="checkbox"
                checked={filters.hide_invoiced}
                onChange={(e) =>
                  onUpdateFilter("hide_invoiced", e.target.checked)
                }
              />
              Hide already-invoiced details
            </label>
          </div>
        );

      case "s111_action_filter":
        return (
          <div className="filter-dropdown-content">
            <Select
              name="action_filter"
              label=""
              options={actionFilterOptions}
              value={filters.action_filter}
              onChange={(value) =>
                onUpdateFilter("action_filter", value as OptionType | null)
              }
              isClearable
              placeholder="Select action..."
            />
          </div>
        );

      case "s111_nsa_filter":
        return (
          <div className="filter-dropdown-content">
            <input
              type="text"
              value={filters.nsa_filter?.value?.toString() || ""}
              onChange={(e) => {
                const val = e.target.value.toUpperCase().trim();
                onUpdateFilter(
                  "nsa_filter",
                  val ? { label: val, value: val } : null,
                );
              }}
              placeholder="Enter NSA code (e.g. RA)"
              className="filter-search-input"
              style={{ marginTop: 0 }}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="finance-filter-dropdown" ref={dropdownRef}>
      <div className="filter-dropdown-search">
        <input
          type="search"
          placeholder="Filter..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="filter-search-input"
        />
      </div>

      <div className="filter-dropdown-list">
        {visibleCategories.map((category) => {
          const config = FILTER_CATEGORIES[category];
          const isExpanded = expandedCategory === category;

          return (
            <div key={category} className="filter-category">
              <button
                className={`filter-category-header ${isExpanded ? "expanded" : ""}`}
                onClick={() => handleCategoryClick(category)}
                type="button"
              >
                <span className="material-icons category-icon">
                  {config.icon}
                </span>
                <span className="category-label">{config.label}</span>
                {config.hasSubOptions && (
                  <span className="material-icons expand-icon">
                    {isExpanded ? "expand_more" : "chevron_right"}
                  </span>
                )}
              </button>

              {isExpanded && renderCategoryContent(category)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FinanceFilterDropdown;
